import { API, graphqlOperation, Amplify } from "aws-amplify";
import { getAppSyncPlusConfig } from './AWSServices';
import { listPreference, createPreference, updatePreference } from './AppSyncPlusAPIs';
import { v4 as uuidv4 } from 'uuid';

const TILE_LAYOUT_PREFERENCE_TYPE = "TILE_LAYOUT";
const ASSOCIATION_TYPE = "APP";

// Configure Amplify to use the Plus API for tile layout operations
const configurePlusAPI = async () => {
  const awsAppSyncPlus = await getAppSyncPlusConfig();
  Amplify.configure(awsAppSyncPlus.Appsync);
};

export const saveTileLayout = async (appId, userId, layoutData, preference, onSuccess, onFailure) => {
  // Normalize columnOffset to ensure it has all breakpoint keys
  const normalizedLayoutData = layoutData.map(item => ({
    id: item.id,
    columnSpan: item.columnSpan,
    rowSpan: item.rowSpan,
    columnOffset: normalizeColumnOffset(item.columnOffset)
  }));

  const associationId = `${appId }`;
  const associationIdType = ASSOCIATION_TYPE;
  const label = `Tile Layout for ${appId}`;

  if (preference && preference.preferenceId) {
    // Preference exists - UPDATE
    updatePreference(
      preference.preferenceId,
      label,
      userId,
      normalizedLayoutData,
      (updateResponse) => {
        // Return the full preference object in the response
        const updatedPreference = {
          ...preference,
          information: normalizedLayoutData,
          lastUpdatedBy: userId,
          lastUpdatedTimestamp: Date.now().toString(),
          label: label
        };
        if (onSuccess) onSuccess({ ...updateResponse, preference: updatedPreference });
      },
      (updateError) => {
        if (onFailure) onFailure(updateError);
      }
    );
  } else {
    const newPreferenceId = uuidv4();
    const newPreference = {
      preferenceId: newPreferenceId,
      preferenceType: TILE_LAYOUT_PREFERENCE_TYPE,
      associationId: associationId,
      associationIdType: associationIdType,
      information: normalizedLayoutData,
      label: label,
      createdBy: userId,
      lastUpdatedBy: userId,
      createdTimestamp: Date.now().toString(),
      lastUpdatedTimestamp: Date.now().toString()
    };
    
    createPreference(
      newPreferenceId,
      TILE_LAYOUT_PREFERENCE_TYPE,
      label,
      associationId,
      associationIdType,
      userId,
      normalizedLayoutData,
      null, // ttl
      (createResponse) => {
        if (onSuccess) onSuccess({ ...createResponse, preference: newPreference });
      },
      (createError) => {
        if (onFailure) onFailure(createError);
      }
    );
  }
};


const normalizeColumnOffset = (columnOffset) => {
  if (!columnOffset || typeof columnOffset !== 'object') {
    return { xxs: 0, xs: 0, s: 0, m: 0, l: 0, xl: 0 };
  }
  
  // If it already has the expected keys, return as is
  if ('xxs' in columnOffset || 'xs' in columnOffset) {
    return {
      xxs: columnOffset.xxs ?? 0,
      xs: columnOffset.xs ?? 0,
      s: columnOffset.s ?? 0,
      m: columnOffset.m ?? 0,
      l: columnOffset.l ?? 0,
      xl: columnOffset.xl ?? 0
    };
  }
  
  // If it has numeric keys (from Board component internal representation), 
  // try to extract the value and apply to all breakpoints
  const values = Object.values(columnOffset);
  const offsetValue = values.length > 0 ? values[0] : 0;
  
  return {
    xxs: offsetValue,
    xs: offsetValue,
    s: offsetValue,
    m: offsetValue,
    l: offsetValue,
    xl: offsetValue
  };
};


export const loadTileLayout = async (appId, userId, onSuccess, onFailure) => {
  const associationId = `${appId }`;

  
  listPreference(
    associationId,
    ASSOCIATION_TYPE,
    TILE_LAYOUT_PREFERENCE_TYPE,
    (result) => {
      const preferences = result?.data?.listPreference?.items || [];
    
      const matchingPreference = preferences.find(pref => {
        return pref.associationId === associationId;
      });

      if (matchingPreference && matchingPreference.information) {
        const layoutData = matchingPreference.information;
        const preferenceId = matchingPreference.preferenceId;
        
        try {
          // The information field should already be parsed as an object
          const parsedData = typeof layoutData === 'string' ? JSON.parse(layoutData) : layoutData;
          if (Array.isArray(parsedData)) {
            // Normalize columnOffset for each item
            const normalizedData = parsedData.map(item => ({
              id: item.id,
              columnSpan: item.columnSpan,
              rowSpan: item.rowSpan,
              columnOffset: normalizeColumnOffset(item.columnOffset)
            }));
       
            if (onSuccess) onSuccess({ 
              layoutData: normalizedData, 
              preferenceId,
              preference: matchingPreference
            });
          } else {
            // If it's null or empty, no saved layout exists
            if (onSuccess) onSuccess({ layoutData: null, preferenceId: null, preference: null });
          }
        } catch (parseError) {
          console.error('Error parsing layout data:', parseError);
          if (onSuccess) onSuccess({ layoutData: null, preferenceId: null, preference: null });
        }
      } else {
        // No saved layout found
        if (onSuccess) onSuccess({ layoutData: null, preferenceId: null, preference: null });
      }
    },
    (error) => {
      console.error('Error loading tile layout preference:', error);

      if (onSuccess) onSuccess({ layoutData: null, preferenceId: null, preference: null });
    }
  );
};
